import { dwEmittable, LogLevel, fetchRequest } from '@darwin/common';
export { CommonEvents, LogLevel } from '@darwin/common';
import { __awaiter } from 'tslib';

/**
 * Enum for the events that the config module can emit
 * and can be listened from outside the module
 */
var ConfigEvents;
(function (ConfigEvents) {
    ConfigEvents["CONFIG_LOADED"] = "configLoaded";
})(ConfigEvents || (ConfigEvents = {}));

const CONFIG = 'config/config.json';
const emitMixin = dwEmittable()();
/**
 * The config class is the main class of the configuration module.
 *
 * It is responsible for recovering and unifying the different configuration file,
 * prevailing architectural configuration over local.
 *
 * The configuration file should be located inside the config folder located in the root
 * of application.
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * const config: Config = new Config();
 * ```
 */
class Config extends emitMixin {
    constructor(logLevel = LogLevel.ERROR) {
        super();
        this._dwEmitter.logLevel = logLevel;
    }
    /**
     * Gets the loaded config.
     * @returns A deepCopy of config property
     */
    get config() {
        this._dwEmitter.emitLogDebug('Config::config - Reading the config property');
        if (!this._config) {
            const warningMsg = 'Config::config - It seems the configuration has not been loaded.' +
                'This may generate errors!!';
            this._dwEmitter.emitLogWarning(warningMsg);
            return this._config;
        }
        return JSON.parse(JSON.stringify(this._config));
    }
    /**
     * Loads the architecture´s configuration from config.json.
     * @returns Promise that resolves to json config
     * ### Example
     * ``` ts
     * const config: Config = new Config();
     * config.load().then((configuration) => {
     *  console.log(configuration);
     * });
     * ```
     */
    load() {
        return __awaiter(this, void 0, void 0, function* () {
            const debugInfo = 'Config::load -';
            this._dwEmitter.emitLogInfo(`${debugInfo} Loading configuration...`);
            try {
                this._config = yield getConfig(this._dwEmitter);
                this._dwEmitter.emitLogInfo(`${debugInfo} Configuration has been loaded in Config module!`);
            }
            catch (error) {
                this._dwEmitter.emitError(error);
                throw error;
            }
            return JSON.parse(JSON.stringify(this._config));
        });
    }
}
/**
 * Get configuration from JSON file.
 */
function getConfig(log) {
    return new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
        try {
            const debugInfo = 'Config::getConfig -';
            log.emitLogDebug(`${debugInfo} Trying to get the configuration from the server...`);
            const jsonConfig = yield getJsonFile(log);
            log.emitLogDebug(`${debugInfo} Configuration has been got from the server!`);
            resolve(jsonConfig);
            log.emit(ConfigEvents.CONFIG_LOADED, JSON.parse(JSON.stringify(jsonConfig)));
        }
        catch (error) {
            reject(error);
        }
    }));
}
/**
 * Get json file from url (config.json)
 * @param log Log interface with log function
 * @returns Promise<Json> Promise that returns the config file
 */
function getJsonFile(log) {
    return __awaiter(this, void 0, void 0, function* () {
        const debugInfo = 'Config::getJsonFile -';
        log.emitLogDebug(
        // tslint:disable-next-line:max-line-length
        `${debugInfo} Doing fetch request... ${CONFIG}`);
        const jsonFile = yield fetchRequest(CONFIG);
        log.emitLogDebug(`${debugInfo} File successfully loaded:
    config: ${JSON.stringify(jsonFile)}`);
        return jsonFile;
    });
}

export { Config, ConfigEvents };
//# sourceMappingURL=config.js.map
